import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AuthService from "./service/AmplifyAuth";
import { useAppContext } from "./context/AppContext";
import { openViews, useNavigate, useRouteMatch } from "./router";
import Layout from "./layout/Layout";
import Dashboard from "./views/Dashboard";
import UserAccount from "./views/UserAccount";
import Contact from "./views/Contact";
import TopUp from "./views/TopUp";
import FAQ from "./views/FAQ";
import GIG from "./views/GIG";
import ChangePassword from "./views/ChangePassword";
import ChangeEmail from "./views/ChangeEmail";
import UserInfo from "./views/UserInfo";
import CheckScore from "./views/CheckScore";
import PastReviews from "./views/PastReviews";
import PastGuestFormRequests from "./views/PastGuestFormRequests";
import HostLogin from "./views/HostLogin";
import SignUp from "./views/SignUp";
import GuestForm from "./views/GuestForm";
import QuickScoreResults from "./views/QuickScoreResults";
import InvitationCode from "./views/InvitationCode";
import Listings from "./views/Listings";

const isApiUrlAllowed = (url) => {
  if (typeof url !== "string" || !url.length) return false;

  // Relative API call
  if (url.startsWith("/v1")) return true;

  // Absolute same-origin API call
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith("/v1");
  } catch {
    return false;
  }
};

const guardedViews = {
  dashboard: Dashboard,
  account: UserAccount,
  listings: Listings,
  contact: Contact,
  topup: TopUp,
  faq: FAQ,
  gig: GIG,
  password: ChangePassword,
  email: ChangeEmail,
  userinfo: UserInfo,
  checkscore: CheckScore,
  reviews: PastReviews,
  "guest-form-requests": PastGuestFormRequests,
};

function renderOpenRoute(route) {
  switch (route.view) {
    case "login":
      return <HostLogin />;
    case "signup":
      return <SignUp code={route.params.code} />;
    case "guest-form":
      return <GuestForm id={route.params.id} />;
    case "quick-score-results":
      return <QuickScoreResults reference={route.params.reference} query={route.query} />;
    case "invitation-code":
      return <InvitationCode />;
    default:
      return <HostLogin />;
  }
}

function App() {
  const route = useRouteMatch();
  const navigate = useNavigate();
  const { state, commit } = useAppContext();
  const [isUIBlocked, setIsUIBlocked] = useState(false);

  const isGuardNeeded = useMemo(() => openViews.indexOf(route.name) < 0, [route.name]);
  const hasStoredSession = localStorage.getItem("asdasd123123dfgdfgqweqwe") !== null;

  useEffect(() => {
    const requestId = axios.interceptors.request.use(
      (config) => {
        if (!isApiUrlAllowed(config.url || "")) {
          return Promise.reject(new Error(`Blocked non-API request URL: ${String(config.url)}`));
        }
        console.log("onRequest", config);
        return config;
      },
      (error) => {
        setIsUIBlocked(false);
        return Promise.reject(error);
      },
    );

    const responseId = axios.interceptors.response.use(
      (response) => {
        setIsUIBlocked(false);
        return response;
      },
      (error) => {
        setIsUIBlocked(false);
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
    };
  }, []);

  useEffect(() => {
    if (window.location.href.includes("/guestForm/")) return;

    AuthService.currentAuthenticatedUser()
      .then((user) => {
        if (user != null) commit("signInUser", user);
      })
      .catch((error) => {
        console.error("No Authenticated user found", error);
      });
  }, [commit]);

  useEffect(() => {
    if (!(state.isUserSignedIn || hasStoredSession) && isGuardNeeded) {
      console.error("not logged in yet, view is guarded, login required: ", route.name);
      navigate("/login");
    }
  }, [hasStoredSession, isGuardNeeded, navigate, route.name, state.isUserSignedIn]);

  if (!isGuardNeeded) {
    return (
      <>
        {isUIBlocked ? <div className="block-ui-overlay" /> : null}
        {renderOpenRoute(route)}
      </>
    );
  }

  if (!(state.isUserSignedIn || hasStoredSession)) {
    return (
      <>
        {isUIBlocked ? <div className="block-ui-overlay" /> : null}
        <HostLogin />
      </>
    );
  }

  const GuardedView = guardedViews[route.view] || Dashboard;

  return (
    <>
      {isUIBlocked ? <div className="block-ui-overlay" /> : null}
      <Layout>
        <GuardedView />
      </Layout>
    </>
  );
}

export default App;
